import { setError, setLoaded, setLoading, updateState, withCallState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, effect, inject, resource } from '@angular/core';
import { signalStore, withState, withMethods, withHooks, withProps, type, signalMethod, withComputed } from '@ngrx/signals';
import { withEntities, setAllEntities, EntityId, updateEntity, removeEntity, addEntity } from '@ngrx/signals/entities';
import { RadiantService } from '../services/radiant.services';
import { firstValueFrom } from 'rxjs';
import { Loan, Contact, InitContact, Profile, InitProfile, Bank, InitBank, Insurance, InitInsurance, InsuranceLoan, Rider } from '../models';
import { withEntitiesLocalFilter, withEntitiesLocalPagination, withEntitiesLocalSort } from '@ngrx-traits/signals';
import { Injectable } from '@angular/core';
import { NotificationUtils } from '../utils/notification.utils';

export interface BaseEntity {
    id: EntityId;
    name?: string;
    [key: string]: any;
}

interface StoreState<T, L, R> {
    selected: T | null;
    selectedLoan: L | null;
    selectedChild: R | null;
    filter: {
        search: string;
    };
    search: string;
}

// The store interface that components can use for typing
export interface IEntityStore<T extends BaseEntity, L extends BaseEntity, R extends BaseEntity> {
    entities: () => T[];
    error: () => string | null;
    capitalize: (str: string) => string;
    filterEntities: (params: { filter: { search: string } }) => void;
    sortEntities: (params: { sort: { field: string; direction: 'asc' | 'desc' | '' } }) => void;
    delete: (entity: T) => void;
    deleteChild: <R extends BaseEntity = T>(params: { child: R, endpoint?: string, collectionName?: string }) => void;
    create: <M extends BaseEntity = T>(params: { entity: Omit<M, 'id'>, endpoint?: string }) => void;
    update: <M extends BaseEntity = T>(entity: M, endpoint?: string) => void;
    loadEntitiesPage: (event: any) => void;
    entitiesCurrentPage: () => { entities: T[]; total: number; pageSize: number; pageIndex: number };
    selected: () => T | null;
    selectedLoan: () => L | null;
    selectedChild: () => R | null;
    selectEntity: (entity: T | null) => void;
    getUserById: (id: number) => T | null;
    updateAll: <M extends BaseEntity = T>(entity: M, collectionName: string) => void;
    getModel: () => any;
}

export function createStore<T extends BaseEntity, L extends BaseEntity, R extends BaseEntity>(name: string, plural_name: string, model: any) {
    const entity = type<T>();

    const initialState: StoreState<T, L, R> = {
        selected: null,
        selectedLoan: null,
        selectedChild: null,
        filter: {
            search: ''
        },
        search: '',
    }
    
    const store = signalStore(
        { providedIn: 'root' },
        withState<StoreState<T, L, R>>(initialState),
        withEntities({entity}),
        withDevtools(plural_name),
        withCallState(),
        withEntitiesLocalPagination({ entity, pageSize: 25 }),
        withEntitiesLocalFilter({
            entity,
            defaultFilter: { search: '' },
            filterFn: (entity: T, filter:any) => {
                if (!filter?.search) {
                    return true;
                }
                const searchValue = filter.search.toLowerCase();

                // Recursive function to get all searchable values from an object
                const getSearchableValues = (obj: any): string[] => {
                    if (!obj || typeof obj !== 'object') {
                        return [];
                    }

                    return Object.entries(obj).flatMap(([key, value]) => {
                        // Skip id fields and null/undefined values
                        if (key === 'id' || value == null) {
                            return [];
                        }

                        if (typeof value === 'object') {
                            // Recursively search nested objects
                            return getSearchableValues(value);
                        }

                        // Convert value to string and return if it's a primitive
                        return String(value).toLowerCase();
                    });
                };

                const searchableValues = getSearchableValues(entity);
                return searchableValues.some(value => value.includes(searchValue));
            }
        }),
        withEntitiesLocalSort({
            entity,
            defaultSort: { field: 'name', direction: 'asc' },
            sortFunction: (entities: T[], sort: { field: string; direction: 'asc' | 'desc' | '' }) => {
              if (!sort?.field) return entities
        
              return entities.sort((a, b) => {
                // Handle address fields
                // Handle regular location fields
                const aValue = a[sort.field as keyof Location]
                const bValue = b[sort.field as keyof Location]
                
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                  return sort.direction === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue)
                }
                
                const numA = Number(aValue) || 0
                const numB = Number(bValue) || 0
                return sort.direction === 'asc' ? numA - numB : numB - numA
              })
            }
        }),
        withProps(() => ({_radiantService: inject(RadiantService)})),
        withProps(() => ({_notificationUtils: inject(NotificationUtils)})),
        withProps((store) => ({
            _resource: resource({
                request: computed(() => ({
                    filter: store.filter,
                    sort: store.sort
                })),
                loader: async() => {
                    try {
                        updateState(
                            store, 
                            'Loading ' + name + ' entities', 
                            setLoading(null)
                        )
                        const entities = await firstValueFrom(
                        store._radiantService.getAll<T>(plural_name) 
                        )
                        if (entities) {
                        updateState(
                            store, 
                            name[0].toUpperCase() + name.slice(1) + ' entities loaded successfully', 
                            setAllEntities(entities),
                            setLoaded(null)
                        )
                        }
                        return entities
                    } catch (error) {
                        updateState(
                            store, 
                            'Error loading ' + name + ' entities',
                            setLoaded(null),
                            setError('Failed to load ' + name, null)
                        )
                        console.log(error.message)
                        throw error
                    }
                }
            })
        })),
        withMethods((store) => ({
            getLoansByState(state: string) {
                const selected = store.selected();
                if (!selected || !selected.loans_collection) {
                    return [];
                }
                return selected.loans_collection.filter(t => t.state == state);
            },
            selectEntity: signalMethod((entity: T | null) => {
                updateState(store, 'Select entity', { selected: entity }, setLoaded(null))
            }),
            selectLoan: signalMethod((loan: L | null) => {
                updateState(store, 'Select loan', { selectedLoan: loan })
            }),
            selectChild: signalMethod((child: R | null) => {
                updateState(store, 'Select child', { selectedChild: child })
            }),
            capitalize(str: string): string {
                return str ? str[0].toUpperCase() + str.substring(1) : str;
            },
            getEntity(id: number): T {
               return store.entities().find(t => t.id == id) as T 
            },
            getByUserId(id: number): T {
                return store.entities().find(t => t.user_id == id) as T 
             },
            create: signalMethod(
                async <M extends BaseEntity = T>(params: { entity: Omit<M, 'id'>, endpoint?: string }) => {
                    const { entity, endpoint } = params;
                    try {
                        updateState(
                            store, 
                            'Start creating ' + endpoint,
                            setLoading(null)
                        )
                        const newEntity = await firstValueFrom(
                            store._radiantService.create<M>(endpoint, entity)
                        )
                        if (newEntity) {
                            updateState(
                                store,
                                endpoint[0].toUpperCase() + endpoint.slice(1) + ' entity created successfully',
                                addEntity(newEntity as unknown as T, null),
                                setLoaded(null),
                                { 
                                    selected: null 
                                }
                            )
                            store._notificationUtils.showActionResult(true, endpoint, 'created');
                        }
                    } 
                    catch (error) {
                        updateState(
                            store,
                            'Error creating ' + endpoint,
                            setLoaded(null),
                            setError('Failed to create ' + endpoint, null)
                        )
                        store._notificationUtils.showActionResult(false, endpoint, 'created');
                        throw error
                    }
                }
            ),
            createLoan: signalMethod(
                async (params: { loan: Omit<L, 'id'>, endpoint?: string, collectionName?: string }) => {
                    const { loan, endpoint, collectionName} = params;
                    try {
                        updateState(
                            store, 
                            'Start creating loan component ' + endpoint[0].toUpperCase() + endpoint.slice(1),
                            setLoading(null)
                        )
                        const newEntity = await firstValueFrom(
                            store._radiantService.create<L>(endpoint, loan)
                        )
                        if (newEntity) {
                            // Get the current selected entity details
                            const selected = store.selected() as T & { id: EntityId };
                            if (selected) {                          
                                // Create updated details with new child
                                const updatedDetails = {
                                    ...selected,
                                    [collectionName]: [...(selected[collectionName] || []), newEntity]
                                };
                                
                                // Update the selected entity with the new child
                                updateState(
                                    store,
                                    endpoint[0].toUpperCase() + endpoint.slice(1) + ' Loan Entity Created Successfully',
                                    setLoaded(null),
                                    updateEntity({ id: selected.id, changes: updatedDetails }),
                                    { 
                                        selected: updatedDetails,
                                        selectedLoan: newEntity,
                                        selectedChild: null
                                    }
                                )
                                store._notificationUtils.showActionResult(true, endpoint, 'updated');
                            }
                        }
                    } 
                    catch (error) {
                        updateState(
                            store,
                            'Error creating loan component' + endpoint,
                            setLoaded(null),
                            setError('Failed to create loan component ' + endpoint, null)
                        )
                        store._notificationUtils.showActionResult(false, endpoint, 'updated');
                        throw error
                    }
                }
            ),
            createChild: signalMethod(
                async <R extends BaseEntity = T>(params: { child: Omit<R, 'id'>, endpoint?: string, collectionName?: string }) => {
                    const { child, endpoint, collectionName} = params;
                    try {
                        updateState(
                            store, 
                            'Start creating child component ' + endpoint[0].toUpperCase() + endpoint.slice(1),
                            setLoading(null)
                        )
                        const newEntity = await firstValueFrom(
                            store._radiantService.create<L>(endpoint, child)
                        )
                        if (newEntity) {
                            // Get the current selected entity details
                            const selected = store.selected() as T & { id: EntityId };
                            if (selected) {                          
                                // Create updated details with new child
                                const updatedDetails = {
                                    ...selected,
                                    [collectionName]: [...(selected[collectionName] || []), newEntity]
                                };
                                
                                // Update the selected entity with the new child
                                updateState(
                                    store,
                                    endpoint[0].toUpperCase() + endpoint.slice(1) + ' Child Entity Created Successfully',
                                    setLoaded(null),
                                    updateEntity({ id: selected.id, changes: updatedDetails }),
                                    { 
                                        selected: updatedDetails,
                                        // selectedLoan: null,
                                        // selectedChild: null
                                    }
                                )
                                store._notificationUtils.showActionResult(true, endpoint, 'updated');
                            }
                        }
                    } 
                    catch (error) {
                        updateState(
                            store,
                            'Error creating child component' + endpoint,
                            setLoaded(null),
                            setError('Failed to create child component ' + endpoint, null)
                        )
                        store._notificationUtils.showActionResult(false, endpoint, 'updated');
                        throw error
                    }
                }
            ),
            update: signalMethod(
                async <M extends BaseEntity = T>(entity: M) => {
                    try {
                        updateState(
                            store, 
                            'Start updating ' + name,
                            setLoading(null)
                        )
                        const updatedEntity = await firstValueFrom(
                            store._radiantService.update<M>(name, +entity.id, entity)
                        )
                        if (updatedEntity) {
                            // We need to cast the entity to T since the store operates on type T
                            const entityAsT = updatedEntity as unknown as T;
                            updateState(
                                store, 
                                name[0].toUpperCase() + name.slice(1) + ' entity updated successfully',
                                updateEntity({ id: entity.id, changes: entityAsT }),
                                setLoaded(null),
                                { 
                                    selected: null 
                                }
                            )
                            store._notificationUtils.showActionResult(true, name, 'updated');
                        }
                    } 
                    catch (error) {
                        updateState(
                            store, 
                            'Error Updating ' + name[0].toUpperCase() + name.slice(1),
                            setLoaded(null),
                            setError('Failed to update ' + name[0].toUpperCase() + name.slice(1), null)
                        )
                        store._notificationUtils.showActionResult(false, name, 'updated');
                        throw error
                    }
                }
            ),
            updateLoan: signalMethod(
                async <L extends BaseEntity = T>(params: { loan: L, endpoint?: string, collectionName?: string }) => {
                    const { loan, endpoint, collectionName } = params
                    try {
                        updateState(
                            store, 
                            'Start updating component ' + endpoint,
                            setLoading(null)
                        )
                        const updatedLoan = await firstValueFrom(
                            store._radiantService.update<L>(endpoint, +loan.id, loan)
                        )
                        if (updatedLoan) {
                            // Get the current selected entity details
                            const selected = store.selected() as T & { id: EntityId };
                            if (selected) {
                                // Find and update the child in the collection
                                const collection = selected[collectionName] || [];
                                // Ensure collection is an array before mapping
                                const updatedCollection = Array.isArray(collection) ? collection.map(item => 
                                    item.id === loan.id ? updatedLoan : item
                                ) : [];
                                
                                // Create updated details with modified child
                                const updatedDetails = {
                                    ...selected,
                                    [collectionName]: updatedCollection
                                };
                                // Update the selected entity with the modified child
                                updateState(
                                    store,
                                    endpoint[0].toUpperCase() + endpoint.slice(1) + ' entity updated successfully',
                                    setLoaded(null),
                                    { 
                                        selected: updatedDetails,
                                        // selectedLoan: null, man will this affect other pages?
                                        // selectedChild: null
                                    }
                                )
                                store._notificationUtils.showActionResult(true, endpoint, 'updated');
                            }
                        }
                    } 
                    catch (error) {
                        updateState(
                            store,
                            'Error updating child component ' + endpoint,
                            setLoaded(null),
                            setError('Failed to update child component ' + endpoint, null)
                        )
                        store._notificationUtils.showActionResult(false, endpoint, 'updated');
                        throw error
                    }
                }
            ),
            updateChild: signalMethod(
                async <R extends BaseEntity = T>(params: { child: R, endpoint?: string, collectionName?: string }) => {
                    const { child, endpoint, collectionName } = params
                    try {
                        updateState(
                            store, 
                            'Start updating component ' + endpoint,
                            setLoading(null)
                        )
                        const updatedChild = await firstValueFrom(
                            store._radiantService.update<R>(endpoint, +child.id, child)
                        )
                        if (updatedChild) {
                            // Get the current selected entity details
                            const selected = store.selected() as T & { id: EntityId };
                            if (selected) {
                                // Find and update the child in the collection
                                const collection = selected[collectionName] || [];
                                const updatedCollection = collection.map(item => 
                                    item.id === child.id ? {...updatedChild} : item
                                );
                                
                                // Create updated details with modified child
                                const updatedDetails = {
                                    ...selected,
                                    [collectionName]: updatedCollection
                                };
                                // Update the selected entity with the modified child
                                updateState(
                                    store,
                                    endpoint[0].toUpperCase() + endpoint.slice(1) + ' entity updated successfully',
                                    setLoaded(null),
                                    { 
                                        selected: updatedDetails,
                                        selectedLoan: null,
                                        selectedChild: null
                                    }
                                )
                                store._notificationUtils.showActionResult(true, endpoint, 'updated');
                            }
                        }
                    } 
                    catch (error) {
                        updateState(
                            store,
                            'Error updating child component ' + endpoint,
                            setLoaded(null),
                            setError('Failed to update child component ' + endpoint, null)
                        )
                        store._notificationUtils.showActionResult(false, endpoint, 'updated');
                        throw error
                    }
                }
            ),
            updateAll: signalMethod(
                async <R extends BaseEntity = T>(params: { child: R, endpoint: string, collectionName: string, cutoffDate?: string }) => {
                    const { child, endpoint, collectionName, cutoffDate } = params
                    try {
                        const ret = await firstValueFrom(
                            store._radiantService.update(endpoint, child.user_id, { cutoffDate })
                        )
                        if (ret) {
                            // Get the current selected entity details
                            const selected = store.selected() as T & { id: EntityId };
                            if (selected) {
                                // Update items in the collection based on cutoff date
                                const collection = selected[collectionName] || [];
                                const updatedCollection = collection.map(item => {
                                    // Only mark as transferred if no cutoff date is specified or if the item's date is on or before the cutoff date
                                    if (!cutoffDate || (item.transaction_date && item.transaction_date <= cutoffDate)) {
                                        return {
                                            ...item,
                                            transferred: true
                                        };
                                    }
                                    return item;
                                });
                                
                                // Create updated details with modified collection
                                const updatedDetails = {
                                    ...selected,
                                    [collectionName]: updatedCollection
                                };
                                
                                // Update the selected entity with the modified collection
                                updateState(
                                    store,
                                    endpoint[0].toUpperCase() + endpoint.slice(1) + ' entity updated successfully',
                                    setLoaded(null),
                                    { 
                                        selected: updatedDetails,
                                        selectedLoan: null,
                                        selectedChild: null
                                    }
                                )
                                store._notificationUtils.showActionResult(true, endpoint, 'updated');
                            }
                        }
                    } 
                    catch (error) {
                        updateState(
                            store,
                            'Error updating child component ' + endpoint,
                            setLoaded(null),
                            setError('Failed to update child component ' + endpoint, null)
                        )
                        store._notificationUtils.showActionResult(false, endpoint, 'updated');
                        throw error
                    }
                }
            ),
            delete: signalMethod(
                async <M extends BaseEntity = T>(entity: M) => {
                    try {
                        const delEntity = await firstValueFrom(
                            store._radiantService.delete(name, +entity.id)
                        )
                        if (delEntity) {
                            updateState(
                                store, 
                                name[0].toUpperCase() + name.slice(1) + ' deleted successfully',
                                removeEntity(+entity.id, null),
                            );
                            store._notificationUtils.showActionResult(true, name, 'deleted');
                        }
                        else {
                            updateState(
                                store, 
                                'Error Deleting ' + name[0].toUpperCase() + name.slice(1),
                                setError('Failed to Delete ' + name[0].toUpperCase() + name.slice(1), null)
                            );
                            store._notificationUtils.showActionResult(false, name, 'deleted');
                        }
                    } catch (error) {
                        updateState(
                            store, 
                            'Error Deleting ' + name[0].toUpperCase() + name.slice(1),
                            setError('Failed to Delete ' + name[0].toUpperCase() + name.slice(1), null)
                        );
                        store._notificationUtils.showActionResult(false, name, 'deleted');
                        throw error;
                    }
                }
            ),
            deleteLoan: signalMethod(
                (params: { loan: L, endpoint: string, collectionName: string }) => {
                    const { loan, endpoint, collectionName } = params;
                    try {
                        // Get the current selected entity details
                        const selected = store.selected() as T & { id: EntityId };
                        if (!selected) {
                            return
                        }

                        // Get the current collection
                        const collection = selected[collectionName] || [];

                        // Remove the entity from the collection
                        const updatedCollection = collection.filter((item: any) => item.id !== loan.id);

                        const del = firstValueFrom(
                            store._radiantService.delete(endpoint, +loan.id)
                        );

                        // Update the selected details with the new collection
                        if (del) {
                            updateState(
                                store, 
                                'Delete ' + endpoint + ' Entity',
                                { 
                                    selected: {
                                        ...selected,
                                        [collectionName]: updatedCollection
                                    },
                                    selectedLoan: null,
                                    selectedChild: null
                                }
                            );
                            store._notificationUtils.showActionResult(true, endpoint, 'deleted');
                        }
                        else {
                            updateState(
                                store, 
                                'Error Deleting ' + endpoint[0].toUpperCase() + endpoint.slice(1),
                                setError('Failed to Delete ' + endpoint + ' Entity', null)
                            );
                            store._notificationUtils.showActionResult(false, endpoint, 'deleted');
                        }
                    } catch (error) {
                        updateState(
                            store, 
                            'Error Deleting ' + endpoint[0].toUpperCase() + endpoint.slice(1),
                            setError('Failed to Delete ' + endpoint + ' Entity', null)
                        );
                        store._notificationUtils.showActionResult(false, endpoint, 'deleted');
                        throw error
                    }
                }
            ),
            deleteChild: signalMethod(
                async <R extends BaseEntity = T>(params: { child: R, endpoint?: string, collectionName?: string }) => {
                    const { child, endpoint, collectionName } = params;
                    try {
                        // Get the current selected entity details
                        const selected = store.selected() as T & { id: EntityId };
                        if (!selected) {
                            return
                        }

                        // Get the current collection
                        const collection = selected[collectionName] || [];

                        // Remove the entity from the collection
                        const updatedCollection = collection.filter((item: any) => item.id !== child.id);

                        const del = firstValueFrom(
                            store._radiantService.delete(endpoint, +child.id)
                        );

                        // Update the selected details with the new collection
                        if (del) {
                            updateState(
                                store, 
                                'Delete ' + endpoint + ' Entity',
                                { 
                                    selected: {
                                        ...selected,
                                        [collectionName]: updatedCollection
                                    },
                                    selectedLoan: null,
                                    selectedChild: null
                                }
                            );
                            store._notificationUtils.showActionResult(true, endpoint, 'deleted');
                        }
                        else {
                            updateState(
                                store, 
                                'Error Deleting ' + endpoint[0].toUpperCase() + endpoint.slice(1),
                                setError('Failed to Delete ' + endpoint + ' Entity', null)
                            );
                            store._notificationUtils.showActionResult(false, endpoint, 'deleted');
                        }
                    } catch (error) {
                        updateState(
                            store, 
                            'Error Deleting ' + endpoint[0].toUpperCase() + endpoint.slice(1),
                            setError('Failed to Delete ' + endpoint + ' Entity', null)
                        );
                        store._notificationUtils.showActionResult(false, endpoint, 'deleted');
                        throw error
                    }
                }
            ),
            getModel: () => model,          
        })),
        withHooks({
            onInit(store) {
              // Handle errors
              effect(() => {
                const error = store._resource.error()
                if (error) {
                  console.error('Location loading error:', error)
                }
              })
            }
        })
    );

    // Create an injectable service that extends the store
    @Injectable({ providedIn: 'root' })
    class Store extends store {}

    return Store;
}


// Create singleton store instances
@Injectable({ providedIn: 'root' })
export class ContactStore extends createStore<Contact, null, null>('contact', 'contacts', InitContact) {}

@Injectable({ providedIn: 'root' })
export class BankStore extends createStore<Bank, Loan, null>('bank', 'banks', InitBank) {}

@Injectable({ providedIn: 'root' })
export class InsuranceStore extends createStore<Insurance, InsuranceLoan, Rider>('insurance', 'insurances', InitInsurance) {}

@Injectable({ providedIn: 'root' })
export class UserStore extends createStore<Profile, null, null>('user', 'users', InitProfile) {}